import { ISign, Sign } from '@app/models/Sign'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface ISignsListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<ISign>
  readonly orderBy: string
  readonly sortOrder: string
  readonly totalItems: number
}

const SignsListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<Sign>(),
  orderBy: 'Ascending',
  sortOrder: '',
  totalItems: 0,
})

export class SignsListState
  extends SignsListStateRecord
  implements ISignsListState
{
  public static fromJS(initialState?: Partial<ISignsListState>) {
    if (!initialState) {
      return new SignsListState()
    }

    const { items, ...rest } = initialState
    return new SignsListState({
      items: items ? List(items.map((i) => Sign.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<Sign>
  public readonly orderBy: string
  public readonly sortOrder: string
  public readonly totalItems: number

  constructor(props?: Partial<SignsListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum SignsListActionTypes {
  LoadSigns = 'SIGNS_LIST_REPORT::LOAD_SIGNS',
  LoadSignsFailure = 'SIGNS_LIST_REPORT::LOAD_SIGNS_FAILURE',
  LoadSignsRequest = 'SIGNS_LIST_REPORT::LOAD_SIGNS_REQUEST',
  LoadSignsSuccess = 'SIGNS_LIST_REPORT::LOAD_SIGNS_SUCCESS',
  SetAssignedTo = 'SIGNS_LIST_REPORT::SET_ASSIGNED_TO',
  SetCity = 'SIGNS_LIST_REPORT::SET_CITY',
  SetClient = 'SIGNS_LIST_REPORT::SET_CLIENT',
  SetDueDate = 'SIGNS_LIST_REPORT::SET_DUE_DATE',
  SetFilterText = 'SIGNS_LIST_REPORT::SET_FILTER_TEXT',
  SetOrderBy = 'SIGNS_LIST_REPORT::SET_ORDER_BY',
  SetScope = 'SIGNS_LIST_REPORT::SET_SCOPE',
  SetStatus = 'SIGNS_LIST_REPORT::SET_STATUS',
  SetSortOrder = 'SIGNS_LIST_REPORT::SET_SORT_ORDER',
}

interface ILoadSignsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: SignsListActionTypes.LoadSigns
}

interface ILoadSignsFailureAction {
  error: string
  type: SignsListActionTypes.LoadSignsFailure
}

interface ILoadSignsRequestAction {
  type: SignsListActionTypes.LoadSignsRequest
}

interface ILoadSignsSuccessAction {
  append: boolean
  items: Sign[]
  totalItems: number
  type: SignsListActionTypes.LoadSignsSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: SignsListActionTypes.SetFilterText
}

interface ISetOrderByAction {
  orderBy: string
  type: SignsListActionTypes.SetOrderBy
}

interface ISetSortOrderAction {
  type: SignsListActionTypes.SetSortOrder
  value: string
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadSignsFailureAction
  | ILoadSignsRequestAction
  | ILoadSignsSuccessAction
  | ISetOrderByAction
  | ISetFilterTextAction
  | ISetSortOrderAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadSigns(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadSignsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: SignsListActionTypes.LoadSigns,
  }
}

function loadSignsFailure(error: string): ILoadSignsFailureAction {
  return {
    error,
    type: SignsListActionTypes.LoadSignsFailure,
  }
}

function loadSignsRequest(): ILoadSignsRequestAction {
  return {
    type: SignsListActionTypes.LoadSignsRequest,
  }
}

function loadSignsSuccess(
  items: Sign[],
  append: boolean,
  totalItems: number,
): ILoadSignsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: SignsListActionTypes.LoadSignsSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: SignsListActionTypes.SetFilterText,
  }
}

function setOrderBy(orderBy: string): ISetOrderByAction {
  return {
    orderBy,
    type: SignsListActionTypes.SetOrderBy,
  }
}

function setSortOrder(value: string): ISetSortOrderAction {
  return {
    type: SignsListActionTypes.SetSortOrder,
    value,
  }
}

export const actionCreators = {
  loadSigns,
  setFilterText,
  setOrderBy,
  setSortOrder,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<SignsListState> = (
  state: SignsListState = new SignsListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case SignsListActionTypes.LoadSignsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as SignsListState

    case SignsListActionTypes.LoadSignsRequest:
      return state.set('isLoading', true) as SignsListState

    case SignsListActionTypes.LoadSignsSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Sign>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as SignsListState

    case SignsListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as SignsListState

    case SignsListActionTypes.SetOrderBy:
      return state.set('orderBy', action.orderBy) as SignsListState

    case SignsListActionTypes.SetSortOrder:
      return state.set('sortOrder', action.value) as SignsListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadSignsSaga(action: ILoadSignsAction) {
  try {
    yield put(loadSignsRequest())

    const currentState: SignsListState = yield select(
      (state: ApplicationState) => state.signsList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${currentState.filterText}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    if (currentState.orderBy) {
      query.push(`orderBy=${currentState.orderBy}`)
    }

    if (currentState.sortOrder) {
      query.push(`sort=${currentState.sortOrder}`)
    } else {
      query.push(`sort=Property`)
    }

    const baseUrl = '/api/signs'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: Sign[] = result.results.map((c: any) => Sign.fromJS(c))
      yield put(loadSignsSuccess(contacts, false, result.count))
    } else {
      yield put(loadSignsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadSignsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadSigns() {
  yield takeLatest(SignsListActionTypes.LoadSigns, loadSignsSaga)
}

export function* signsListRootSaga() {
  yield fork(watchLoadSigns)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): SignsListState =>
    state.signsList

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectFilterText = () =>
  createSelector(selectModuleState(), (mState) => mState.filterText)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectItems = () =>
  createSelector(selectModuleState(), (mState) => mState.items)

export const selectOrderBy = () =>
  createSelector(selectModuleState(), (mstate) => mstate.orderBy)

export const selectSort = () =>
  createSelector(selectModuleState(), (mState) => mState.sortOrder)

export const selectTotalItems = () =>
  createSelector(selectModuleState(), (mState) => mState.totalItems)
